import React, { useState, useRef } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Text,
  Image,
  Dimensions,
  Animated,
  Platform,
  Alert,
  ActivityIndicator
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import * as ImagePicker from 'expo-image-picker';

import { useAuth } from '../../contexts/AuthContext';
import * as authApi from '../../api/auth';
import { H1, H2, H3, Body1, Body2, Caption, ButtonText } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';
import { getProfileImageUrl } from '../../utils/imageUtils';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const ViewProfileScreen = () => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { user, logout, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [updatingPhoto, setUpdatingPhoto] = useState(false);
  const scrollY = useRef(new Animated.Value(0)).current;

  const headerHeight = 220;

  // Helper function to safely parse occupation details
  const parseDetails = (data) => {
    if (!data) return {};
    if (typeof data === 'object') return data;
    try {
      return JSON.parse(data);
    } catch (e) {
      return {};
    }
  };

  const schoolDetails = parseDetails(user?.schoolDetails);
  const employmentDetails = parseDetails(user?.employmentDetails);
  const businessDetails = parseDetails(user?.businessDetails);

  const handleLogout = () => {
    Alert.alert(
      'Logout',
      'Are you sure you want to logout?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Logout',
          style: 'destructive',
          onPress: async () => {
            try {
              setLoading(true);
              await authApi.logoutUser();
              await logout();
            } catch (error) {
              setLoading(false);
              Alert.alert('Error', 'Failed to logout. Please try again.');
            }
          }
        }
      ]
    );
  };

  const handlePickImage = async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission Denied', 'We need camera roll permissions to update your photo.');
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.7,
      });

      if (!result.canceled && result.assets[0]) {
        uploadImage(result.assets[0]);
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to pick image');
    }
  };

  const uploadImage = async (asset) => {
    try {
      setUpdatingPhoto(true);
      const imageData = {
        uri: asset.uri,
        type: 'image/jpeg',
        name: 'profile.jpg'
      };

      // Use the existing updateUser context which handles the API call
      // Passing empty object as first arg (userData) and image as second arg (profileImage)
      await updateUser({}, imageData);
      Alert.alert('Success', 'Profile photo updated successfully');
    } catch (error) {
      Alert.alert('Upload Failed', error.message || 'Error uploading image');
    } finally {
      setUpdatingPhoto(false);
    }
  };

  const renderSection = (title, icon, content) => (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Ionicons name={icon} size={16} color="#4f46e5" />
        <H3 style={styles.sectionTitle}>{title}</H3>
      </View>
      <View style={styles.infoCard}>
        {content}
      </View>
    </View>
  );

  const InfoRow = ({ label, value, icon, isLast }) => (
    <View style={[styles.infoRow, isLast && { borderBottomWidth: 0 }]}>
      <View style={styles.infoRowIcon}>
        <Ionicons name={icon} size={14} color="#6366F1" />
      </View>
      <View style={styles.infoRowText}>
        <Caption style={styles.infoLabel}>{label}</Caption>
        <Body2 style={styles.infoValue}>{value || 'Not provided'}</Body2>
      </View>
    </View>
  );

  const isMentor = !!(
    user?.role?.toLowerCase() === 'mentor' ||
    user?.role?.toLowerCase() === 'admin' ||
    user?.isMentor === true ||
    user?.isMentor === 1
  );

  return (
    <View style={styles.container}>
      <StatusBar style="light" />

      {/* Header Aura Background */}
      <View style={[styles.headerContainer, { height: headerHeight }]}>
        <LinearGradient
          colors={['#1e1b4b', '#4338ca', '#6366f1']}
          style={StyleSheet.absoluteFill}
        />
        <View style={styles.auraContainer}>
          <View style={[styles.aura, styles.aura1]} />
          <View style={[styles.aura, styles.aura2]} />
        </View>

        <View style={[styles.profileHeaderContent, { paddingTop: insets.top + 15 }]}>
          <View style={styles.avatarWrapper}>
            <View style={styles.avatarContainer}>
              <View style={styles.avatarRing}>
                {user?.profileImageUrl || user?.profileImage ? (
                  <Image
                    source={{ uri: getProfileImageUrl(user.profileImageUrl || user.profileImage) }}
                    style={styles.avatarImage}
                  />
                ) : (
                  <View style={styles.placeholderAvatar}>
                    <H1 style={styles.placeholderText}>{user?.name?.charAt(0) || 'U'}</H1>
                  </View>
                )}
              </View>
              <TouchableOpacity
                style={styles.editPhotoBadge}
                onPress={handlePickImage}
                disabled={updatingPhoto}
              >
                {updatingPhoto ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Ionicons name="camera" size={14} color="#FFFFFF" />
                )}
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.nameContainer}>
            <H2 style={styles.profileName}>{user?.name}</H2>
            <View style={styles.badgeContainer}>
              <View style={[
                styles.roleBadge,
                isMentor && { backgroundColor: '#7c3aed', borderColor: '#a78bfa' }
              ]}>
                <Caption style={[styles.roleBadgeText, isMentor && { color: '#ffffff' }]}>
                  {isMentor ? (user?.role?.toUpperCase() || 'MENTOR') : (user?.occupation?.toUpperCase() || 'MEMBER')}
                </Caption>
              </View>
            </View>
          </View>
        </View>
      </View>

      <Animated.ScrollView
        contentContainerStyle={[styles.scrollContent, { paddingTop: headerHeight - 30 }]}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: false }
        )}
        scrollEventThrottle={16}
      >
        <View style={styles.contentSurface}>
          {/* Account Details */}
          {renderSection('Account Details', 'person-outline', (
            <>
              <InfoRow icon="mail-outline" label="Email Address" value={user?.email} />
              <InfoRow icon="call-outline" label="Phone Number" value={user?.phone} />
              <InfoRow icon="calendar-outline" label="Member Since" value="January 2026" isLast={true} />
            </>
          ))}

          {/* Location & Organization */}
          {renderSection('Organization', 'business-outline', (
            <>
              <InfoRow
                icon="location-outline"
                label="FCP Location"
                value={user?.participant?.fcp?.name || 'Main Campus'}
              />
              <InfoRow
                icon="grid-outline"
                label="Cluster"
                value={user?.participant?.fcp?.cluster?.name || 'Central Region'}
              />
              <InfoRow
                icon="id-card-outline"
                label="Participant No."
                value={user?.participant?.participantNumber || 'P-000000'}
                isLast={true}
              />
            </>
          ))}

          {/* Occupation Details */}
          {user?.occupation && renderSection('Current Occupation', 'briefcase-outline', (
            <View>
              {user.occupation === 'student' && (
                <>
                  <InfoRow icon="school-outline" label="Institution" value={schoolDetails?.schoolName} />
                  <InfoRow icon="book-outline" label="Course" value={schoolDetails?.course} isLast={true} />
                </>
              )}
              {user.occupation === 'employed' && (
                <>
                  <InfoRow icon="business-outline" label="Company" value={employmentDetails?.companyName} />
                  <InfoRow icon="ribbon-outline" label="Position" value={employmentDetails?.position} isLast={true} />
                </>
              )}
              {user.occupation === 'business' && (
                <>
                  <InfoRow icon="storefront-outline" label="Business" value={businessDetails?.businessName} />
                  <InfoRow icon="globe-outline" label="Industry" value={businessDetails?.industry} isLast={true} />
                </>
              )}
            </View>
          ))}

          {/* Quick Actions at Bottom */}
          <TouchableOpacity
            style={[styles.actionButton, { flex: 0, width: '100%', marginBottom: 12, height: 48 }]}
            onPress={() => navigation.navigate('Feedback')}
          >
            <View style={[styles.actionGradient, { backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E2E8F0' }]}>
              <Ionicons name="chatbubble-ellipses-outline" size={18} color="#6366F1" />
              <ButtonText style={[styles.actionText, { color: '#6366F1' }]}>Share Your Feedback</ButtonText>
            </View>
          </TouchableOpacity>

          <View style={[styles.actionsRow, { marginTop: 0, marginBottom: 40 }]}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => navigation.navigate('EditProfile')}
            >
              <LinearGradient colors={['#6366F1', '#4F46E5']} style={styles.actionGradient}>
                <Ionicons name="create" size={16} color="#FFFFFF" />
                <ButtonText style={styles.actionText}>Edit Profile</ButtonText>
              </LinearGradient>
            </TouchableOpacity>

            <TouchableOpacity style={styles.logoutBtn} onPress={handleLogout}>
              <Ionicons name="log-out-outline" size={16} color="#EF4444" />
              <ButtonText style={styles.logoutBtnText}>Logout</ButtonText>
            </TouchableOpacity>
          </View>
        </View>
      </Animated.ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1e1b4b', // Match header colors
  },
  headerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    overflow: 'hidden',
  },
  auraContainer: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.5,
  },
  aura: {
    position: 'absolute',
    borderRadius: 200,
  },
  aura1: {
    width: 250,
    height: 250,
    backgroundColor: '#818cf8',
    top: -120,
    right: -80,
    opacity: 0.4,
  },
  aura2: {
    width: 200,
    height: 200,
    backgroundColor: '#c084fc',
    bottom: -80,
    left: -40,
    opacity: 0.3,
  },
  profileHeaderContent: {
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  avatarWrapper: {
    marginBottom: 10,
  },
  avatarContainer: {
    position: 'relative',
  },
  avatarRing: {
    width: 86,
    height: 86,
    borderRadius: 43,
    borderWidth: 3,
    borderColor: 'rgba(255,255,255,0.2)',
    padding: 3,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 40,
  },
  placeholderAvatar: {
    width: '100%',
    height: '100%',
    borderRadius: 40,
    backgroundColor: '#4338ca',
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholderText: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 28,
  },
  editPhotoBadge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#6366F1',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#1e1b4b',
    ...getShadow('sm'),
  },
  nameContainer: {
    alignItems: 'center',
  },
  profileName: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '800',
  },
  badgeContainer: {
    flexDirection: 'row',
    marginTop: 6,
    gap: 6,
  },
  roleBadge: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  roleBadgeText: {
    color: '#e0e7ff',
    fontWeight: '700',
    fontSize: 9,
  },
  scrollContent: {
    flexGrow: 1,
  },
  contentSurface: {
    backgroundColor: '#EEF2FF', // Deeper, more dynamic Indigo tint
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    paddingTop: 32,
    paddingHorizontal: 18,
    minHeight: SCREEN_WIDTH,
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 20,
  },
  actionButton: {
    flex: 2,
    height: 42,
    borderRadius: 12,
    overflow: 'hidden',
    ...getShadow('md'),
  },
  actionGradient: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  actionText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 14,
  },
  logoutBtn: {
    flex: 1,
    height: 42,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#FEE2E2',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    ...getShadow('sm'),
  },
  logoutBtnText: {
    color: '#EF4444',
    fontWeight: '700',
    fontSize: 14,
  },
  section: {
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
    paddingLeft: 4,
  },
  sectionTitle: {
    color: '#1e1b4b',
    fontSize: 14,
  },
  infoCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    padding: 15,
    ...getShadow('sm'),
    borderWidth: 1,
    borderColor: '#F1F5F9',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F8FAFC',
  },
  infoRowIcon: {
    width: 30,
    height: 30,
    borderRadius: 10,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  infoRowText: {
    flex: 1,
  },
  infoLabel: {
    color: '#94A3B8',
    marginBottom: 1,
    fontSize: 10,
  },
  infoValue: {
    color: '#1E293B',
    fontWeight: '700',
    fontSize: 13,
  },
});

export default ViewProfileScreen;
